import React, {useEffect, useState} from 'react';
import moment from "moment-timezone";
import {makeApiCall} from "../../Api/makeApiCall";
import Page from "../Components/Page";
import {LoadingWrapper} from "../Components/LoadingWrapper";
import ReactTable from "../Components/ReactTable";
import FileLink from "../IngestionLogs/Columns/FileLink";
import {RETAILER_ID_NAME_COLUMN} from "../Components/ReactTable/common_custom_columns";
import ReactTableFilterDropdown from "../ReactTable/ReactTableFilterDropdown";
import {BOOLEAN_DROPDOWN_OPTIONS} from "../Components/ReactTable/common_dropdown_options";
import SweeperDagStatus from "../IngestionLogs/SweeperDagStatus";
import {SelectableList} from "../Components/SelectableList";
import {CHICAGO_TIMEZONE} from "../Alerts/MissingData/filters";
import {faIcons} from "../../utils/iconConstants";
import {getJsonParamOrDefault, updateQueryStringForState} from "../../utils/urlBuilder";

const BYTES_PER_MB = 1024 ** 2;

const toTwelveHourTime = (timeCreated) => timeCreated
    ? moment(timeCreated, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DD h:mm:ss A")
    : '';

const toAgeInDays = (timeCreated) => timeCreated
    ? moment.tz(CHICAGO_TIMEZONE).startOf('day')
        .diff(moment.tz(timeCreated, "YYYY-MM-DD HH:mm:ss", CHICAGO_TIMEZONE).startOf('day'), 'days')
    : undefined;

const totalFilesPerFtp = (incomingFiles) => Object.entries(
    incomingFiles.reduce((acc, {ftp}) => ({...acc, [ftp]: (acc[ftp] ?? 0) + 1}), {})
)
    .map(([ftp, totalFiles]) => ({label: ftp, value: totalFiles}))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));

const isPausedFromUrl = () => {
    const isPaused = getJsonParamOrDefault('isPaused', undefined);
    return typeof isPaused === 'boolean' ? [{id: 'isPaused', value: isPaused}] : [];
};

const IncomingBucketDetails = () => {

    const [incomingFiles, setIncomingFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [selectedFtp, setSelectedFtp] = useState(undefined);
    const [ftpInfoHovered, setFtpInfoHovered] = useState(false);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/listGcsFiles?url=gs://spins-edp-${process.env.REACT_APP_ENV}-incoming/`),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/ftpToRetailerIdDecode/`),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/ingestionPausedRetailers/`)
        ])
        .then(([
            incomingFilesResponse,
            ftpToRetailerResponse,
            ingestionPausedRetailersResponse,
        ])=> {
            const apiError = [incomingFilesResponse, ftpToRetailerResponse, ingestionPausedRetailersResponse]
                .find((response) => response?.apiError);
            if (apiError) {
                throw apiError.apiError;
            }
            const ftpToRetailerId = Object.fromEntries(ftpToRetailerResponse.data
                .map((result) => [result.name?.toLowerCase(), result.retailerId]));
            const pausedRetailers = ingestionPausedRetailersResponse.data;
            const files = incomingFilesResponse
                .map((file) => {
                    const ftp = file.folderPath.split("/")[0]
                    const lowerCaseFtp = ftp.toLowerCase();
                    const retailerId = ftpToRetailerId[lowerCaseFtp];
                    const isPaused = pausedRetailers.some((paused) =>
                        paused.ftp ? `${paused.ftp}`.toLowerCase() === lowerCaseFtp : paused.retailerId === retailerId);
                    const timeCreated = file?.timeCreatedChicago?.substring(0, 19);
                    return {
                        fullFilePath: file?.url,
                        fileName: file?.fileName,
                        ftp,
                        retailerId,
                        isPaused,
                        timeCreated,
                        ageInDays: toAgeInDays(timeCreated),
                        sizeInMb: Number((file?.sizeInBytes / BYTES_PER_MB).toFixed(2))
                    }
                });
            setIncomingFiles(files);
        }).catch((error) => {
            console.error("=============> error: ", error);
            setError(true);
        })
            .finally(() => setLoading(false));

    }, []);

    const columns = [
        RETAILER_ID_NAME_COLUMN,
        {
            header: "Is Paused",
            accessorKey: "isPaused",
            cell: ({getValue}) => getValue() ? 'True' : 'False',
            filterFn: (row, columnId, filterValue) => row.getValue(columnId) === filterValue,
            filter: (theseProps) =>
                <ReactTableFilterDropdown
                    id={"isPaused"}
                    options={BOOLEAN_DROPDOWN_OPTIONS}
                    onChange={(selected) => {
                        theseProps.column.setFilterValue(selected ?? undefined);
                        updateQueryStringForState({isPaused: selected ?? undefined});
                    }}
                    selectedValue={theseProps.column.getFilterValue()}
                    isClearable={true}
                />
        },
        {header: "FTP", accessorKey: "ftp"},
        {
            header: "Path",
            accessorKey: "fullFilePath",
            filterFn: (row, columnIds, filterValue) => {
                return row.original?.fullFilePath.toUpperCase().includes(filterValue.toUpperCase());
            },
            cell: ({getValue}) =>
                <FileLink
                    displayText={getValue()}
                    gcsFileLocation={getValue()}
                />
        },
        {header: "File", accessorKey: "fileName"},
        {
            header: "Time Created",
            accessorKey: "timeCreated",
            cell: ({getValue}) => toTwelveHourTime(getValue()),
            filterFn: (row, columnId, filterValue) =>
                toTwelveHourTime(row.getValue(columnId)).toUpperCase().includes(filterValue.toUpperCase())
        },
        {header: "Age (Days)", accessorKey: "ageInDays"},
        {header: "Size (MB)", accessorKey: "sizeInMb", cell: ({getValue}) => getValue().toFixed(2)}
    ]
    return <Page name={"Incoming Bucket Details"}>
        <SweeperDagStatus/>
        <div className={'incoming-bucket-details-actions'}>
            {!loading && !error &&
                <div className={'tooltip-icon incoming-files-per-ftp-info'}
                     data-testid={'incoming-files-per-ftp-info'}
                     onMouseEnter={() => setFtpInfoHovered(true)}
                     onMouseLeave={() => setFtpInfoHovered(false)}>
                     <i className={faIcons.infoCircle} aria-label={'Files per FTP'}/>
                    {ftpInfoHovered &&
                        <div className={'tooltip-contents incoming-files-per-ftp'} data-testid={'incoming-files-per-ftp'}>
                            <h6>Files Per FTP</h6>
                            <SelectableList items={totalFilesPerFtp(incomingFiles)}
                                            selected={selectedFtp}
                                            onItemSelected={setSelectedFtp}/>
                        </div>}
                </div>}
            <a className={'button hollow secondary'}
               href={'/ingestionPausedRetailers'}
               target={'_blank'}
               rel={'noopener noreferrer'}>Ingestion Paused Retailers</a>
        </div>
        <LoadingWrapper loading={loading}>
            {error
                ? <div className={'error-message'} data-testid={'error-message'}>There was an error loading the data.
                    Please try again later.</div>
                : <ReactTable
                    data={selectedFtp ? incomingFiles.filter(({ftp}) => ftp === selectedFtp.label) : incomingFiles}
                    columns={columns}
                    filterable={true}
                    initialState={{sorting: [{id: "isPaused", desc: false}], columnFilters: isPausedFromUrl()}}
                />}
        </LoadingWrapper>
    </Page>
};

export default IncomingBucketDetails;